import { getSectorColor } from '@/lib/sector-colors';
import { dotRadius, labelSize, labelOpacity, mktCapWeight, type PlotStock } from '@/lib/scatter2d-helpers';

interface Props {
  sortedStocks: PlotStock[];
  highlightSymbol: string;
  pinnedSymbols: Set<string>;
  hoveredSymbol: string | null;
  visibleLabels: Set<string>;
  enteredAlphaSymbols?: Set<string>;
  exitedAlphaSymbols?: Set<string>;
}

export function TickerLabelsLayer({
  sortedStocks,
  highlightSymbol,
  pinnedSymbols,
  hoveredSymbol,
  visibleLabels,
  enteredAlphaSymbols,
  exitedAlphaSymbols,
}: Props) {
  return (
    <>
      {sortedStocks.map(s => {
        const isHighlighted = !!(highlightSymbol && s.symbol === highlightSymbol);
        const isHovered = hoveredSymbol === s.symbol;
        const isPinned = pinnedSymbols.has(s.symbol);

        const isEnterAlpha = enteredAlphaSymbols?.has(s.symbol) ?? false;
        const isExitAlpha = exitedAlphaSymbols?.has(s.symbol) ?? false;
        const isSignal = isEnterAlpha || isExitAlpha;
        if (!isHighlighted && !isHovered && !isPinned && !isSignal && !visibleLabels.has(s.symbol)) return null;

        const color = getSectorColor(s.sector);
        const sz = labelSize(s.marketCap, isHighlighted, isHovered, isPinned);
        const baseOp = labelOpacity(s.marketCap, isHighlighted, isHovered, isPinned);
        const op = (highlightSymbol && !isHighlighted && !isPinned) ? (isHovered ? 0.3 : 0.08) : baseOp;
        const isSpecial = isHighlighted || isHovered || isPinned;
        const labelColor = isSpecial ? '#fff' : isEnterAlpha ? '#22c55e' : isExitAlpha ? '#dc2626' : color;
        return (
          <text
            key={`lbl-${s.symbol}`}
            x={s.px + dotRadius(s.marketCap, isHighlighted, isHovered, isPinned) + 2}
            y={s.py + sz * 0.35}
            fill={labelColor}
            fontSize={isSignal && !isSpecial ? Math.max(sz, 9) : sz}
            fontFamily="var(--font-mono), ui-monospace, monospace"
            fontWeight={isSpecial ? 700 : isSignal ? 700 : mktCapWeight(s.marketCap)}
            opacity={op}
            filter={isSpecial ? 'url(#textshadow)' : undefined}
          >
            {isSignal && !isSpecial && (
              <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" />
            )}
            {s.symbol}
          </text>
        );
      })}
    </>
  );
}
